import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import Select from '@pattern-library/select';
import { getOptionValue, getOptionLabel, isOptionDisabled } from './usePlanPickerApi/items';
import { PlanPickerProvider, usePlanPickerContext } from './PlanPickerContext';
import VirtualizedPlanPickerMenuList from './VirtualizedPlanPickerMenuList';
import PlanPickerOption from './PlanPickerOption';
import PlanPickerInput from './PlanPickerInput';

function PlanPickerSelect(textContent) {
    const { label, placeholder, loadingMessage, noOptionsMessage } = textContent;

    const {
        mode,
        dataStrategy,
        options = [],
        total,
        isWaitingForInitialData,
        updateSearchQuery,
        selectedId,
        value,
        onChange,
        selectRef
    } = usePlanPickerContext();

    // Under very specific circumstances, it's a better UX to
    // hide the placeholder temporarily while the initial data
    // is loading. Without this mitigation, the user could see
    // the placeholder flash briefly before the selected plan
    // name replaces it.
    //
    // We reference `selectedId` here rather than `value` because
    // in async mode, value always starts as `null` while we're
    // fetching the initial data. The true indicator of whether
    // there's a selected item or not is whether `selectedId`
    // exists.
    const shouldHidePlaceholder =
        dataStrategy === 'async' &&
        mode === 'list' &&
        isWaitingForInitialData &&
        selectedId !== null;

    return (
        <div className="dca-plan-picker">
            <Select
                id="plan-picker"
                label={label}
                hideLabel
                isSearchable={total === 'unknown' || total > 10}
                placeholder={shouldHidePlaceholder ? '' : placeholder}
                options={options}
                componentsOverride={useMemo(
                    () => ({
                        Input: PlanPickerInput,
                        MenuList:
                            dataStrategy === 'async'
                                ? VirtualizedPlanPickerMenuList
                                : components.MenuList,
                        Option: PlanPickerOption
                    }),
                    [dataStrategy]
                )}
                reactSelectProps={{
                    ref: selectRef,
                    value,
                    onChange,
                    // PlanPickerContext handles filtering for us, either by querying the
                    // server or synchronously filtering the options before passing them down
                    // to this component.
                    filterOption: null,
                    getOptionLabel,
                    getOptionValue,
                    isOptionDisabled,
                    isLoading: isWaitingForInitialData,
                    loadingMessage: useCallback(() => loadingMessage, []),
                    noOptionsMessage: useCallback(() => noOptionsMessage, []),
                    onInputChange: updateSearchQuery
                }}
            />
        </div>
    );
}

/**
 * @param {PlanPickerProps} props
 * @returns {import('./usePlanPickerSideEffects').PlanChangeAction}
 */
const determinePlanChangeAction = ({ onPlanChange, onPlanChangeTargetId, targetId }) => {
    if (onPlanChange) {
        return {
            kind: 'callAngularMethod',
            methodName: onPlanChange,
            targetId: onPlanChangeTargetId
        };
    } else if (targetId) {
        return {
            kind: 'loadHtml',
            targetId
        };
    } else {
        return { kind: 'redirect' };
    }
};

/**
 * @param {PlanPickerProps} props
 */
function PlanPicker(props) {
    const { filterUrl, planDataToken, initialId, initialIndex, items, textContent } = props;

    const sharedProps = {
        action: determinePlanChangeAction(props),
        initialId,
        initialIndex
    };

    /** @type {import('./PlanPickerContext/PlanPickerProvider').PlanPickerProviderProps} */
    const providerProps = filterUrl
        ? {
              ...sharedProps,
              dataStrategy: 'async',
              apiParams: { url: '/' + filterUrl, token: planDataToken }
          }
        : {
              ...sharedProps,
              dataStrategy: 'sync',
              items
          };

    return (
        <PlanPickerProvider {...providerProps}>
            <PlanPickerSelect {...textContent} />
        </PlanPickerProvider>
    );
}

PlanPicker.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
    filterUrl: PropTypes.string,
    planDataToken: PropTypes.string,
    initialId: PropTypes.string,
    initialIndex: PropTypes.number,
    onPlanChange: PropTypes.string,
    onPlanChangeTargetId: PropTypes.string,
    targetId: PropTypes.string,
    textContent: PropTypes.object
};

export default PlanPicker;

/**
 * @typedef {object} PlanPickerProps
 * @prop {import('./usePlanPickerApi/items').RawPlanPickerItem[]} [items]
 * @prop {string} [filterUrl]
 * @prop {string} [planDataToken]
 * @prop {string} [initialId]
 * @prop {number} [initialIndex]
 * @prop {string} [onPlanChange]
 * @prop {string} [onPlanChangeTargetId]
 * @prop {string} [targetId]
 * @prop {PlanPickerTextContent} [textContent]
 */

/**
 * @typedef {object} PlanPickerTextContent
 * @prop {string} label
 * @prop {string} placeholder
 * @prop {string} noOptionsMessage
 * @prop {string} loadingMessage
 */
